import { useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import Sidebar from "./Sidebar";
import Header from "./Header";
import TrialBanner from "@/components/organization/TrialBanner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ChatSocketProvider } from "@/lib/chatSocket";
import { useBillingGate } from "@/hooks/useBillingGate";

interface DashboardLayoutProps {
  children?: ReactNode;
  headerProps?: Record<string, unknown>;
}

export default function DashboardLayout({ children, headerProps = {} }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { needsCard, billingHref } = useBillingGate();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // A new signup starts its trial by putting a card on file; until then the
  // billing page is the only stop.
  if (needsCard && billingHref && pathname !== billingHref) {
    return <Navigate to={billingHref} replace />;
  }
  // …and a full page of its own: no sidebar, search or bell offering places
  // the redirect above would only bounce back from.
  if (needsCard && billingHref) {
    return (
      <div className="min-h-dvh bg-dash-page font-dashboard">
        <header className="flex items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <img src="/logo.svg" alt="Stoop" className="h-9 w-auto" />
          <Button variant="dashboardOutline" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="needs-card-sign-out">Sign out</Button>
        </header>
        <main className="mx-auto w-full max-w-5xl px-4 pb-12 pt-4 sm:px-8 sm:pt-10">{children}</main>
      </div>
    );
  }

  return (
    <ChatSocketProvider>
    <div className="fixed inset-0 flex h-dvh w-full gap-2 overflow-hidden bg-dash-page p-1.5 font-dashboard sm:gap-4 sm:p-2">
      <Sidebar className="hidden lg:flex" />

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[274px] max-w-[80vw] border-r border-dash-border bg-dash-surface p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar className="flex" onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Header onMenuClick={() => setMobileOpen(true)} {...headerProps} />
        <TrialBanner />
        <main className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-3 py-3 pb-6 sm:px-5 sm:py-4 lg:px-5 lg:py-5">
          {children}
        </main>
      </div>
    </div>
    </ChatSocketProvider>
  );
}
